import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import { useLocation, useParams } from "react-router-dom";
import "./SalesReport.css";

function SalesReport() {
  const location = useLocation();
  const { ownerId } = useParams();

  const OWNER_ID = ownerId || location.state?.owner_id;

  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);

  const [filter, setFilter] = useState("today");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");

  const [loading, setLoading] = useState(false);
  const [selectedStall, setSelectedStall] = useState("");
  const [stallList, setStallList] = useState([]);

  // ✅ FIXED: wrapped in useCallback
  const getDateRange = useCallback(() => {
    const now = new Date();
    let start, end;

    if (filter === "today") {
      start = new Date();
      end = new Date();
    } else if (filter === "week") {
      const firstDay = new Date(now.setDate(now.getDate() - now.getDay()));
      start = firstDay;
      end = new Date();
    } else if (filter === "month") {
      start = new Date(now.getFullYear(), now.getMonth(), 1);
      end = new Date();
    } else {
      return { start: customStart, end: customEnd };
    }

    return {
      start: start.toISOString().split("T")[0],
      end: end.toISOString().split("T")[0],
    };
  }, [filter, customStart, customEnd]);

  // ✅ FETCH
  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);

      const { start, end } = getDateRange();

      const res = await axios.get(`http://127.0.0.1:8000/orders/by-date/`, {
        params: { owner_id: OWNER_ID, start_date: start, end_date: end },
      });

      const data = res.data.orders;

      const stalls = [
        ...new Map(data.map((o) => [o.stall_id, o.stall_name])).entries(),
      ].map(([id, name]) => ({ id, name }));

      setStallList(stalls);
      setOrders(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [OWNER_ID, getDateRange]);

  // FILTER
  useEffect(() => {
    if (selectedStall) {
      setFilteredOrders(
        orders.filter((o) => o.stall_id === selectedStall)
      );
    } else {
      setFilteredOrders(orders);
    }
  }, [orders, selectedStall]);

  // FETCH CALL
  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // FLATTEN
  const { rows, totals } = useMemo(() => {
    let finalRows = [];

    let totalNet = 0;
    let totalGST = 0;
    let totalRound = 0;
    let totalPaid = 0;

    filteredOrders.forEach((order) => {
      order.order_details.forEach((item, index) => {
        const net = item.price * item.quantity;

        totalNet += net;
        totalGST += item.gst;

        if (index === 0) {
          totalRound += order.round_off;
          totalPaid += order.total_amount;
        }

        finalRows.push({
          date: index === 0 ? new Date(order.created_datetime).toLocaleString() : "",
          token: index === 0 ? order.token_number : "",
          outlet: index === 0 ? order.stall_name : "",
          item: item.name,
          qty: item.quantity,
          price: item.price,
          net,
          gst: item.gst,
          round: index === 0 ? order.round_off : "",
          total: index === 0 ? order.total_amount : "",
        });
      });
    });

    return {
      rows: finalRows,
      totals: {
        net: totalNet,
        gst: totalGST,
        round: totalRound,
        total: totalPaid,
      },
    };
  }, [filteredOrders]);

  // EXPORT
  const exportToExcel = () => {
    if (!rows.length) return;

    const headers = [
      "Date","Token","Outlet","Item","Qty","Price",
      "Net Amount","GST","Round Off","Total Paid"
    ];

    const csvRows = rows.map((r) => [
      r.date, r.token, r.outlet, r.item,
      r.qty, r.price, r.net, r.gst, r.round, r.total
    ]);

    csvRows.push([
      "TOTAL","","","","","",
      totals.net.toFixed(2),
      totals.gst.toFixed(2),
      totals.round.toFixed(2),
      totals.total.toFixed(2),
    ]);

    const csv =
      "data:text/csv;charset=utf-8," +
      [headers.join(","), ...csvRows.map((r) => r.join(","))].join("\n");

    const link = document.createElement("a");
    link.href = encodeURI(csv);
    link.download = "sales_report.csv";
    link.click();
  };

  const getActiveClass = (type) =>
    filter === type ? "filter-btn active" : "filter-btn";

  return (
    <div className="sales-container">
      <h2 className="title-report">Sales Report</h2>

      <div className="filters">
        <button className={getActiveClass("today")} onClick={() => setFilter("today")}>Today</button>
        <button className={getActiveClass("week")} onClick={() => setFilter("week")}>Week</button>
        <button className={getActiveClass("month")} onClick={() => setFilter("month")}>Month</button>
        <button className={getActiveClass("custom")} onClick={() => setFilter("custom")}>Custom</button>
      </div>

      {filter === "custom" && (
        <div className="custom-date">
          <input type="date" onChange={(e) => setCustomStart(e.target.value)} />
          <input type="date" onChange={(e) => setCustomEnd(e.target.value)} />
          <button onClick={fetchOrders}>Apply</button>
        </div>
      )}

      <div className="stall-filter">
        <select onChange={(e) => setSelectedStall(e.target.value)}>
          <option value="">All Outlets</option>
          {stallList.map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
      </div>

      <button className="export-btn" onClick={exportToExcel}>
        Export Excel
      </button>

      {loading && (
        <div className="loader-wrapper">
          <div className="loader"></div>
          <p>Loading orders...</p>
        </div>
      )}

      {!loading && (
        <div className="table-wrapper">
          <table className="orders-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Token</th>
                <th>Outlet</th>
                <th>Item</th>
                <th>Qty</th>
                <th>Price</th>
                <th>Net</th>
                <th>GST</th>
                <th>Round</th>
                <th>Total</th>
              </tr>
            </thead>

            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td>{r.date}</td>
                  <td>{r.token}</td>
                  <td>{r.outlet}</td>
                  <td>{r.item}</td>
                  <td>{r.qty}</td>
                  <td>₹{r.price}</td>
                  <td>₹{r.net}</td>
                  <td>₹{r.gst}</td>
                  <td>{r.round && `₹${r.round}`}</td>
                  <td>{r.total && `₹${r.total}`}</td>
                </tr>
              ))}

              <tr className="total-row">
                <td colSpan="6">TOTAL</td>
                <td>₹{totals.net.toFixed(2)}</td>
                <td>₹{totals.gst.toFixed(2)}</td>
                <td>₹{totals.round.toFixed(2)}</td>
                <td>₹{totals.total.toFixed(2)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default SalesReport;